// Analytics: GA4 (gtag), Google Tag Manager (dataLayer) and Meta Pixel (fbq).
// GTM and the Meta Pixel base code are loaded in public/index.html.

let GA_MEASUREMENT_ID = null;
let GA_ENABLED = false;
let pageViewCount = 0;

const ATTRIBUTION_KEY = 'apn_attribution';
const ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

/**
 * Initialize Google Analytics (GA4).
 * Automatic page views are disabled; trackPageView sends exactly one per route.
 * @param {string} measurementId - GA4 Measurement ID (e.g., 'G-XXXXXXXXXX')
 */
export function initGoogleAnalytics(measurementId) {
  if (!measurementId) return;

  GA_MEASUREMENT_ID = measurementId;
  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
  }

  window.gtag('config', measurementId, { send_page_view: false });
  GA_ENABLED = true;
}

/**
 * Track one page view per route change.
 * The Meta Pixel base code already fires PageView for the first page load,
 * so the pixel is only called for subsequent in-app navigations.
 */
export function trackPageView(pagePath, pageTitle) {
  pageViewCount += 1;
  const title = pageTitle || document.title || 'Apnaaapan';

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'virtual_page_view', page_path: pagePath, page_title: title });

  if (GA_ENABLED && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: title,
      page_location: window.location.href,
    });
  }

  if (pageViewCount > 1 && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}

/**
 * Track a custom event across GTM, GA4 and (optionally) Meta Pixel.
 * @param {string} name - GA4 event name, e.g. 'generate_lead'
 * @param {object} params - event parameters
 * @param {string} [metaEvent] - Meta standard event, e.g. 'Lead', 'Schedule', 'Contact'
 */
export function trackEvent(name, params = {}, metaEvent) {
  const payload = { ...params, page_path: window.location.pathname };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload });

  if (GA_ENABLED && window.gtag) {
    window.gtag('event', name, payload);
  }

  if (metaEvent && typeof window.fbq === 'function') {
    window.fbq('track', metaEvent, payload);
  }
}

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Storage can be unavailable (private mode, blocked cookies); attribution is best-effort.
  }
}

/**
 * Store first-touch attribution (UTMs, click IDs, landing page, referrer).
 * Later visits with new UTMs update the "last touch" fields only.
 */
export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const touch = {};
  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) touch[key] = value.slice(0, 200);
  });

  let stored = null;
  try {
    stored = JSON.parse(readStorage(ATTRIBUTION_KEY) || 'null');
  } catch (e) {
    stored = null;
  }

  if (!stored) {
    stored = {
      ...touch,
      landing_page: window.location.pathname,
      referrer: document.referrer ? document.referrer.slice(0, 300) : '',
      first_seen: new Date().toISOString(),
    };
  } else if (Object.keys(touch).length) {
    stored.last_touch = touch;
  }

  writeStorage(ATTRIBUTION_KEY, JSON.stringify(stored));
}

/** Attribution data to attach to lead form submissions. */
export function getAttribution() {
  try {
    return JSON.parse(readStorage(ATTRIBUTION_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

const WHATSAPP_RE = /^https?:\/\/(wa\.me|api\.whatsapp\.com)\//i;

/**
 * One document-level listener for contact clicks, so every WhatsApp, phone,
 * email and booking link on the site is tracked without per-component code.
 */
export function initClickTracking() {
  if (window.__apnClickTracking) return;
  window.__apnClickTracking = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest && event.target.closest('a[href], [data-cta]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const location = link.getAttribute('data-cta') || link.closest('header, footer, section')?.tagName.toLowerCase() || 'page';

      if (WHATSAPP_RE.test(href)) {
        trackEvent('whatsapp_click', { cta_location: location }, 'Contact');
      } else if (href.startsWith('tel:')) {
        trackEvent('phone_click', { cta_location: location }, 'Contact');
      } else if (href.startsWith('mailto:')) {
        trackEvent('email_click', { cta_location: location }, 'Contact');
      } else if (href === '/book-call' || link.hasAttribute('data-cta')) {
        trackEvent('cta_click', {
          cta_text: (link.innerText || '').trim().slice(0, 60),
          cta_location: location,
          cta_href: href,
        });
      }
    },
    true
  );
}
